import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toaster';
import { useHasRole } from '@/features/auth/AuthProvider';
import { addBrand, fetchBrands, fetchCategories } from './setupQueries';
import { SearchableSelectWithAdd } from './SearchableSelectWithAdd';
import { CategoryFormDialog } from './CategoryFormDialog';

export function BrandFormDialog({
  open,
  onOpenChange,
  defaultCategoryId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaultCategoryId?: string;
  onCreated?: (brand: { id: string; name: string }) => void;
}) {
  const qc = useQueryClient();
  const canWrite = useHasRole('owner');
  const [categoryId, setCategoryId] = useState('');
  const [name, setName] = useState('');
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);

  const { data: categories = [] } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: fetchCategories,
    enabled: open,
  });

  const categoryOptions = useMemo(
    () =>
      categories.map((c) => ({
        id: c.id,
        label: c.name,
        searchText: c.codePrefix,
      })),
    [categories],
  );

  useEffect(() => {
    if (!open) return;
    setCategoryId(defaultCategoryId ?? categories[0]?.id ?? '');
    setName('');
  }, [open, defaultCategoryId, categories]);

  const save = useMutation({
    mutationFn: async () => {
      if (!categoryId) throw new Error('Select a category');
      if (!name.trim()) throw new Error('Brand name is required');

      const existing = await fetchBrands(categoryId);
      if (existing.some((b) => b.name.toLowerCase() === name.trim().toLowerCase())) {
        throw new Error('This brand already exists in the selected category');
      }

      return addBrand(name.trim(), categoryId);
    },
    onSuccess: (brandId) => {
      qc.invalidateQueries({ queryKey: ['inventory-brands'] });
      toast({ title: 'Brand added', variant: 'success' });
      onCreated?.({ id: brandId, name: name.trim() });
      onOpenChange(false);
    },
    onError: (e) => toast({ title: 'Failed', description: String(e), variant: 'destructive' }),
  });

  if (!canWrite) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add brand</DialogTitle>
            <DialogDescription>
              Brands are linked to a category and appear in the product form when that category is selected.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <SearchableSelectWithAdd
              label="Category *"
              value={categoryId}
              onValueChange={setCategoryId}
              options={categoryOptions}
              placeholder="Select category"
              searchPlaceholder="Search categories…"
              emptyMessage="No categories found"
              onAdd={() => setCategoryFormOpen(true)}
              addTitle="Add category"
            />
            <div>
              <Label>Brand name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Greenply, Fevicol"
                className="mt-1.5"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button variant="gold" disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CategoryFormDialog
        open={categoryFormOpen}
        onOpenChange={setCategoryFormOpen}
        editing={null}
        onCreated={(id) => setCategoryId(id)}
      />
    </>
  );
}
